import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminListFiles,
  approveFile,
  rejectFile,
  listUsers,
  getFileSharedUsers,
  shareFileWithUsers,
} from "../api/admin";
import { deleteFile, reindexFile } from "../api/files";
import { useToast } from "../context/ToastContext";
import { useNavigate } from "react-router-dom";

const STATUS_BADGE = {
  indexed: "bg-green-900/40 text-green-400 border-green-800",
  pending: "bg-yellow-900/40 text-yellow-400 border-yellow-800",
  indexing: "bg-blue-900/40 text-blue-400 border-blue-800",
  failed: "bg-red-900/40 text-red-400 border-red-800",
  pending_approval: "bg-orange-900/40 text-orange-400 border-orange-800",
};

function ShareModal({ file, onClose }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);

  const { data: users } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await listUsers()).data.data,
  });

  const { data: sharedData } = useQuery({
    queryKey: ["file-shared", file.id],
    queryFn: async () => (await getFileSharedUsers(file.id)).data.data,
  });

  useEffect(() => {
    if (sharedData) setSelectedIds(sharedData);
  }, [sharedData]);

  const saveMut = useMutation({
    mutationFn: () => shareFileWithUsers(file.id, selectedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["file-shared", file.id] });
      toast("Sharing updated", "success");
      onClose();
    },
    onError: () => toast("Failed to update sharing", "error"),
  });

  const toggle = (uid) =>
    setSelectedIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );

  const nonAdminUsers = (users || []).filter((u) => u.role !== "admin");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-[400px] rounded-xl bg-dark-surface p-6 border border-dark-border"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-sm font-semibold text-dark-text">Share file</h3>
        <p className="mb-4 text-xs text-dark-muted truncate">{file.original_name}</p>

        <div className="space-y-1 max-h-56 overflow-y-auto mb-4">
          {nonAdminUsers.length === 0 && (
            <p className="text-xs text-dark-muted py-2">No users to share with</p>
          )}
          {nonAdminUsers.map((u) => (
            <label
              key={u.id}
              className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded-lg hover:bg-[#2B2B30] transition-colors"
            >
              <div
                className={`h-4 w-4 flex-shrink-0 flex items-center justify-center rounded border transition-colors ${
                  selectedIds.includes(u.id)
                    ? "bg-accent border-accent"
                    : "border-[#444]"
                }`}
              >
                {selectedIds.includes(u.id) && (
                  <ion-icon name="checkmark" style={{ fontSize: "11px", color: "#fff" }} />
                )}
              </div>
              <input
                type="checkbox"
                className="hidden"
                checked={selectedIds.includes(u.id)}
                onChange={() => toggle(u.id)}
              />
              <div className="min-w-0">
                <p className="text-xs font-medium text-dark-text truncate">
                  {u.name || u.username || u.email.split("@")[0]}
                </p>
                <p className="text-[10px] text-dark-muted truncate">{u.email}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-xs text-dark-muted hover:text-dark-text"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="rounded-lg bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {saveMut.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filterUser, setFilterUser] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [sharingFile, setSharingFile] = useState(null);

  const { data: filesData, error: filesError } = useQuery({
    queryKey: ["admin-files"],
    queryFn: async () => (await adminListFiles()).data.data,
    refetchInterval: (q) => {
      const list = q.state.data || [];
      return list.some((f) => ["pending", "indexing"].includes(f.indexed_status))
        ? 3000
        : false;
    },
  });

  const delFileMut = useMutation({
    mutationFn: deleteFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-files"] });
      toast("File deleted", "success");
    },
    onError: () => toast("Failed to delete file", "error"),
  });

  const reindexMut = useMutation({
    mutationFn: reindexFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-files"] });
      toast("Re-indexing started", "success");
    },
    onError: () => toast("Failed to start re-index", "error"),
  });

  const approveMut = useMutation({
    mutationFn: approveFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-files"] });
      toast("File approved", "success");
    },
  });

  const rejectMut = useMutation({
    mutationFn: rejectFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-files"] });
      toast("File rejected", "success");
    },
  });

  const handleDelete = (f) => {
    if (!window.confirm(`Delete "${f.original_name}"?`)) return;
    delFileMut.mutate(f.id);
  };

  // Unique uploaders for filter dropdown
  const uploaders = [
    ...new Map(
      (filesData || [])
        .filter((f) => f.uploader_email)
        .map((f) => [
          f.uploader_email,
          { email: f.uploader_email, name: f.uploader_name },
        ])
    ).values(),
  ];

  // Apply filter + sort
  let files = [...(filesData || [])];
  if (filterUser !== "all")
    files = files.filter((f) => f.uploader_email === filterUser);
  if (sortBy === "name")
    files.sort((a, b) => (a.original_name || "").localeCompare(b.original_name || ""));
  else if (sortBy === "type")
    files.sort((a, b) => a.file_type.localeCompare(b.file_type));
  // "newest" keeps backend default order

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col">
      {/* Header — Settings page style */}
      <div className="mx-auto w-[90%] max-w-4xl w-full pt-10 pb-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => navigate("/")}
          className="text-dark-muted hover:text-dark-text transition-colors"
        >
          <ion-icon name="arrow-back-outline" style={{ fontSize: "20px" }} />
        </button>
        <h1 className="text-base font-semibold text-dark-text">Files &amp; Documents</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-[90%] max-w-4xl px-0 py-6 space-y-4">
          {/* Filters toolbar */}
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
              All Files
            </h2>
            <div className="flex gap-2">
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="rounded-lg bg-dark-surface border border-dark-border px-3 py-1.5 text-xs text-dark-text outline-none"
              >
                <option value="all">All users</option>
                {uploaders.map((u) => (
                  <option key={u.email} value={u.email}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="rounded-lg bg-dark-surface border border-dark-border px-3 py-1.5 text-xs text-dark-text outline-none"
              >
                <option value="newest">Newest first</option>
                <option value="name">Name A–Z</option>
                <option value="type">Type</option>
              </select>
            </div>
          </div>

          {/* Files table */}
          <div className="card overflow-hidden">
          {filesError && (
            <div className="px-4 py-3 text-sm text-red-400 border-b border-red-900/40 bg-red-900/10">
              Failed to load files:{" "}
              {filesError.response?.data?.message || filesError.message}
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border bg-dark-bg">
                {["Name", "Type", "Status", "Uploaded by", "Date", "Actions"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-dark-muted"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr
                  key={f.id}
                  className="border-b border-dark-border/50 hover:bg-dark-bg/40 transition-colors"
                >
                  <td className="px-4 py-3 text-dark-text max-w-[160px] truncate">
                    {f.original_name}
                  </td>
                  <td className="px-4 py-3 text-dark-muted uppercase text-xs">
                    {f.file_type}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full border px-2 py-0.5 text-xs ${
                        STATUS_BADGE[f.indexed_status] || ""
                      }`}
                    >
                      {f.indexed_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-dark-muted text-xs max-w-[110px] truncate">
                    {f.uploader_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-dark-muted text-xs whitespace-nowrap">
                    {new Date(f.created_at.endsWith("Z") || f.created_at.includes("+") ? f.created_at : f.created_at + "Z").toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 whitespace-nowrap">
                      {f.indexed_status === "pending_approval" ? (
                        <>
                          <button
                            onClick={() => approveMut.mutate(f.id)}
                            className="text-xs text-green-400 hover:text-green-300"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => rejectMut.mutate(f.id)}
                            className="text-xs text-red-400 hover:text-red-300"
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => reindexMut.mutate(f.id)}
                          className="text-xs text-dark-muted hover:text-dark-text"
                        >
                          Re-index
                        </button>
                      )}
                      <button
                        onClick={() => setSharingFile(f)}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        Share
                      </button>
                      <button
                        onClick={() => handleDelete(f)}
                        className="text-xs text-red-500 hover:text-red-400"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!files.length && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-dark-muted text-sm"
                  >
                    No files yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      {sharingFile && (
        <ShareModal file={sharingFile} onClose={() => setSharingFile(null)} />
      )}
    </div>
  );
}
